import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Upload, Trash2, Scale, Droplets, HeartPulse, Smile } from "lucide-react";
import { toast } from "sonner";

export default function Tracking() {
  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      <div className="mb-8 fade-up">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900">Health tracking</h1>
        <p className="text-slate-500 mt-2">Log readings over time. Import from your devices via CSV.</p>
      </div>

      <Tabs defaultValue="weight" className="fade-up delay-1">
        <TabsList className="bg-emerald-50/70 rounded-full p-1.5 mb-6 h-auto flex-wrap">
          <TabsTrigger value="weight" data-testid="tab-weight" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow px-4 py-2 text-sm">
            <Scale className="w-4 h-4 mr-2" /> Weight
          </TabsTrigger>
          <TabsTrigger value="glucose" data-testid="tab-glucose" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow px-4 py-2 text-sm">
            <Droplets className="w-4 h-4 mr-2" /> Glucose
          </TabsTrigger>
          <TabsTrigger value="bp" data-testid="tab-bp" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow px-4 py-2 text-sm">
            <HeartPulse className="w-4 h-4 mr-2" /> Blood Pressure
          </TabsTrigger>
          <TabsTrigger value="adherence" data-testid="tab-adherence" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow px-4 py-2 text-sm">
            <Smile className="w-4 h-4 mr-2" /> Adherence
          </TabsTrigger>
        </TabsList>

        <TabsContent value="weight"><WeightPanel /></TabsContent>
        <TabsContent value="glucose"><GlucosePanel /></TabsContent>
        <TabsContent value="bp"><BPPanel /></TabsContent>
        <TabsContent value="adherence"><AdherencePanel /></TabsContent>
      </Tabs>
    </div>
  );
}

// ============ Weight ============
function WeightPanel() {
  const [rows, setRows] = useState([]);
  const [val, setVal] = useState("");
  const [date, setDate] = useState(today());
  const [note, setNote] = useState("");
  const fileRef = useRef();

  const load = () => api.get("/tracking/weight").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!val) return;
    try {
      await api.post("/tracking/weight", { weight_kg: parseFloat(val), date, note: note || null });
      setVal(""); setNote(""); load();
      toast.success("Logged");
    } catch (e) {
      toast.error("Could not save");
    }
  };

  const del = async (id) => {
    await api.delete(`/tracking/weight/${id}`);
    load();
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/tracking/weight/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.data.inserted} rows`);
      load();
    } catch (err) {
      toast.error("Import failed");
    } finally {
      e.target.value = "";
    }
  };

  const series = useMemo(() => [...rows].reverse().map((r) => ({ date: r.date, v: r.weight_kg })), [rows]);

  return (
    <div className="space-y-6">
      <Card className="rounded-3xl border-emerald-100 p-6">
        <form onSubmit={add} className="grid sm:grid-cols-5 gap-3 items-end" data-testid="weight-form">
          <div className="sm:col-span-1">
            <Label>Weight (kg)</Label>
            <Input type="number" step="0.1" required className="rounded-xl mt-1.5" value={val} onChange={(e) => setVal(e.target.value)} data-testid="weight-input" />
          </div>
          <div className="sm:col-span-1">
            <Label>Date</Label>
            <Input type="date" className="rounded-xl mt-1.5" value={date} onChange={(e) => setDate(e.target.value)} data-testid="weight-date" />
          </div>
          <div className="sm:col-span-2">
            <Label>Note</Label>
            <Input className="rounded-xl mt-1.5" value={note} onChange={(e) => setNote(e.target.value)} placeholder="optional" />
          </div>
          <div className="flex gap-2">
            <Button type="submit" className="rounded-full bg-emerald-600 hover:bg-emerald-700 flex-1" data-testid="weight-add-btn">
              <Plus className="w-4 h-4 mr-1" /> Log
            </Button>
            <Button type="button" variant="outline" className="rounded-full" onClick={() => fileRef.current?.click()} data-testid="weight-import-btn">
              <Upload className="w-4 h-4" />
            </Button>
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={upload} />
          </div>
        </form>
        <p className="text-xs text-slate-500 mt-3">
          CSV columns: <code className="bg-emerald-50 px-1.5 py-0.5 rounded">date,weight_kg,note</code>
        </p>
      </Card>

      <LineChartCard title="Weight over time" data={series} unit=" kg" color="#10b981" testid="weight-chart" />

      <Card className="rounded-3xl border-emerald-100 p-6">
        <h3 className="font-display font-semibold text-slate-900 mb-4">History ({rows.length})</h3>
        <div className="space-y-2" data-testid="weight-list">
          {rows.length === 0 && <div className="text-sm text-slate-400 text-center py-6">No entries yet.</div>}
          {rows.map((r) => (
            <div key={r.id} className="flex items-center justify-between p-3 rounded-xl bg-emerald-50/40">
              <div>
                <div className="font-semibold text-slate-900">{r.weight_kg} kg</div>
                <div className="text-xs text-slate-500">{r.date}{r.note ? ` • ${r.note}` : ""}</div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => del(r.id)} data-testid={`weight-delete-${r.id}`}>
                <Trash2 className="w-4 h-4 text-slate-400" />
              </Button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ============ Glucose ============
function GlucosePanel() {
  const [rows, setRows] = useState([]);
  const [val, setVal] = useState("");
  const [when, setWhen] = useState("fasting");
  const [date, setDate] = useState(today());
  const [time, setTime] = useState(nowHM());
  const [note, setNote] = useState("");
  const fileRef = useRef();

  const load = () => api.get("/tracking/glucose").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tracking/glucose", { value_mgdl: parseFloat(val), when, date, time, note: note || null });
      setVal(""); setNote(""); load();
      toast.success("Logged");
    } catch (e) { toast.error("Could not save"); }
  };

  const del = async (id) => { await api.delete(`/tracking/glucose/${id}`); load(); };

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await api.post("/tracking/glucose/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.data.inserted} rows`); load();
    } catch { toast.error("Import failed"); }
    finally { e.target.value = ""; }
  };

  const series = useMemo(() => [...rows].reverse().map((r) => ({ date: r.date, v: r.value_mgdl })), [rows]);

  return (
    <div className="space-y-6">
      <Card className="rounded-3xl border-emerald-100 p-6">
        <form onSubmit={add} className="grid sm:grid-cols-6 gap-3 items-end" data-testid="glucose-form">
          <div><Label>Value (mg/dL)</Label>
            <Input type="number" required className="rounded-xl mt-1.5" value={val} onChange={(e) => setVal(e.target.value)} data-testid="glucose-input" />
          </div>
          <div><Label>When</Label>
            <Select value={when} onValueChange={setWhen}>
              <SelectTrigger className="rounded-xl mt-1.5" data-testid="glucose-when"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="fasting">Fasting</SelectItem>
                <SelectItem value="before_meal">Before meal</SelectItem>
                <SelectItem value="after_meal">After meal</SelectItem>
                <SelectItem value="bedtime">Bedtime</SelectItem>
                <SelectItem value="random">Random</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Date</Label>
            <Input type="date" className="rounded-xl mt-1.5" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div><Label>Time</Label>
            <Input type="time" className="rounded-xl mt-1.5" value={time} onChange={(e) => setTime(e.target.value)} />
          </div>
          <div><Label>Note</Label>
            <Input className="rounded-xl mt-1.5" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div className="flex gap-2">
            <Button type="submit" className="rounded-full bg-emerald-600 hover:bg-emerald-700 flex-1" data-testid="glucose-add-btn">
              <Plus className="w-4 h-4 mr-1" /> Log
            </Button>
            <Button type="button" variant="outline" className="rounded-full" onClick={() => fileRef.current?.click()} data-testid="glucose-import-btn">
              <Upload className="w-4 h-4" />
            </Button>
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={upload} />
          </div>
        </form>
        <p className="text-xs text-slate-500 mt-3">
          CSV columns: <code className="bg-emerald-50 px-1.5 py-0.5 rounded">date,time,value_mgdl,when,note</code>
        </p>
      </Card>

      <LineChartCard title="Glucose readings" data={series} unit=" mg/dL" color="#0ea5e9" testid="glucose-chart" bands={[{ from: 70, to: 140, color: "#10b98122" }]} />

      <Card className="rounded-3xl border-emerald-100 p-6">
        <h3 className="font-display font-semibold text-slate-900 mb-4">History ({rows.length})</h3>
        <div className="space-y-2" data-testid="glucose-list">
          {rows.length === 0 && <div className="text-sm text-slate-400 text-center py-6">No entries yet.</div>}
          {rows.map((r) => {
            const lvl = r.value_mgdl >= 180 ? "text-red-600" : r.value_mgdl < 70 ? "text-amber-600" : "text-emerald-700";
            return (
              <div key={r.id} className="flex items-center justify-between p-3 rounded-xl bg-emerald-50/40">
                <div>
                  <div className={`font-semibold ${lvl}`}>{r.value_mgdl} mg/dL <span className="text-xs text-slate-500 font-normal">({r.when.replace("_", " ")})</span></div>
                  <div className="text-xs text-slate-500">{r.date} {r.time || ""}{r.note ? ` • ${r.note}` : ""}</div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => del(r.id)}>
                  <Trash2 className="w-4 h-4 text-slate-400" />
                </Button>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

// ============ Blood Pressure ============
function BPPanel() {
  const [rows, setRows] = useState([]);
  const [sys, setSys] = useState("");
  const [dia, setDia] = useState("");
  const [pulse, setPulse] = useState("");
  const [date, setDate] = useState(today());
  const [time, setTime] = useState(nowHM());
  const [note, setNote] = useState("");
  const fileRef = useRef();

  const load = () => api.get("/tracking/blood-pressure").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tracking/blood-pressure", {
        systolic: parseInt(sys), diastolic: parseInt(dia),
        pulse: pulse ? parseInt(pulse) : null,
        date, time, note: note || null,
      });
      setSys(""); setDia(""); setPulse(""); setNote(""); load();
      toast.success("Logged");
    } catch (e) { toast.error("Could not save"); }
  };

  const del = async (id) => { await api.delete(`/tracking/blood-pressure/${id}`); load(); };

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await api.post("/tracking/blood-pressure/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.data.inserted} rows`); load();
    } catch { toast.error("Import failed"); }
    finally { e.target.value = ""; }
  };

  const sysSeries = useMemo(() => [...rows].reverse().map((r) => ({ date: r.date, v: r.systolic })), [rows]);
  const diaSeries = useMemo(() => [...rows].reverse().map((r) => ({ date: r.date, v: r.diastolic })), [rows]);

  return (
    <div className="space-y-6">
      <Card className="rounded-3xl border-emerald-100 p-6">
        <form onSubmit={add} className="grid sm:grid-cols-7 gap-3 items-end" data-testid="bp-form">
          <div><Label>Systolic</Label>
            <Input type="number" required className="rounded-xl mt-1.5" value={sys} onChange={(e) => setSys(e.target.value)} data-testid="bp-sys" />
          </div>
          <div><Label>Diastolic</Label>
            <Input type="number" required className="rounded-xl mt-1.5" value={dia} onChange={(e) => setDia(e.target.value)} data-testid="bp-dia" />
          </div>
          <div><Label>Pulse</Label>
            <Input type="number" className="rounded-xl mt-1.5" value={pulse} onChange={(e) => setPulse(e.target.value)} />
          </div>
          <div><Label>Date</Label>
            <Input type="date" className="rounded-xl mt-1.5" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div><Label>Time</Label>
            <Input type="time" className="rounded-xl mt-1.5" value={time} onChange={(e) => setTime(e.target.value)} />
          </div>
          <div><Label>Note</Label>
            <Input className="rounded-xl mt-1.5" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div className="flex gap-2">
            <Button type="submit" className="rounded-full bg-emerald-600 hover:bg-emerald-700 flex-1" data-testid="bp-add-btn">
              <Plus className="w-4 h-4 mr-1" /> Log
            </Button>
            <Button type="button" variant="outline" className="rounded-full" onClick={() => fileRef.current?.click()} data-testid="bp-import-btn">
              <Upload className="w-4 h-4" />
            </Button>
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={upload} />
          </div>
        </form>
        <p className="text-xs text-slate-500 mt-3">
          CSV columns: <code className="bg-emerald-50 px-1.5 py-0.5 rounded">date,time,systolic,diastolic,pulse,note</code>
        </p>
      </Card>

      <div className="grid lg:grid-cols-2 gap-4">
        <LineChartCard title="Systolic" data={sysSeries} unit=" mmHg" color="#ef4444" testid="bp-sys-chart" />
        <LineChartCard title="Diastolic" data={diaSeries} unit=" mmHg" color="#0ea5e9" testid="bp-dia-chart" />
      </div>

      <Card className="rounded-3xl border-emerald-100 p-6">
        <h3 className="font-display font-semibold text-slate-900 mb-4">History ({rows.length})</h3>
        <div className="space-y-2" data-testid="bp-list">
          {rows.length === 0 && <div className="text-sm text-slate-400 text-center py-6">No entries yet.</div>}
          {rows.map((r) => {
            const high = r.systolic >= 140 || r.diastolic >= 90;
            const elev = !high && (r.systolic >= 130 || r.diastolic >= 80);
            const color = high ? "text-red-600" : elev ? "text-amber-600" : "text-emerald-700";
            return (
              <div key={r.id} className="flex items-center justify-between p-3 rounded-xl bg-emerald-50/40">
                <div>
                  <div className={`font-semibold ${color}`}>{r.systolic}/{r.diastolic} <span className="text-xs text-slate-500 font-normal">mmHg{r.pulse ? ` • ${r.pulse} bpm` : ""}</span></div>
                  <div className="text-xs text-slate-500">{r.date} {r.time || ""}{r.note ? ` • ${r.note}` : ""}</div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => del(r.id)}>
                  <Trash2 className="w-4 h-4 text-slate-400" />
                </Button>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

// ============ Adherence ============
function AdherencePanel() {
  const [date, setDate] = useState(today());
  const [plan, setPlan] = useState(null);
  const [meals, setMeals] = useState([]);
  const [note, setNote] = useState({ mood: "good", energy: "normal", note: "" });
  const [savingNote, setSavingNote] = useState(false);

  const load = async () => {
    const p = await api.get("/meal-plan");
    setPlan(p.data || null);
    const range = await api.get("/tracking/adherence", { params: { start: date, end: date } });
    setMeals(range.data.meals.filter((m) => m.date === date));
    const todayNote = range.data.notes.find((n) => n.date === date);
    if (todayNote) setNote({ mood: todayNote.mood || "good", energy: todayNote.energy || "normal", note: todayNote.note || "" });
    else setNote({ mood: "good", energy: "normal", note: "" });
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  const toggleMeal = async (key, eaten) => {
    await api.post("/tracking/adherence/meal", { date, meal_key: key, eaten });
    load();
  };
  const isEaten = (key) => meals.some((m) => m.meal_key === key && m.eaten);

  const saveNote = async () => {
    setSavingNote(true);
    try {
      await api.post("/tracking/adherence/note", { date, ...note });
      toast.success("Saved");
    } catch (e) {
      toast.error("Could not save");
    } finally {
      setSavingNote(false);
    }
  };

  const items = [
    { key: "breakfast", label: "Breakfast", text: plan?.breakfast?.name },
    { key: "lunch", label: "Lunch", text: plan?.lunch?.name },
    { key: "dinner", label: "Dinner", text: plan?.dinner?.name },
    ...(plan?.snacks || []).map((s, i) => ({ key: `snack-${i}`, label: `Snack ${i + 1}`, text: s.name })),
  ];

  return (
    <div className="space-y-6">
      <Card className="rounded-3xl border-emerald-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-semibold text-slate-900">Daily check-in</h3>
          <Input type="date" className="rounded-xl w-44" value={date} onChange={(e) => setDate(e.target.value)} data-testid="adherence-date" />
        </div>

        {!plan && (
          <div className="text-sm text-slate-500 bg-sky-50/40 rounded-xl p-4 flex items-center justify-between gap-4 flex-wrap">
            <span>Generate your daily meal plan first to track meal adherence.</span>
            <a href="/meal-plan" data-testid="adherence-go-meal-plan">
              <Button size="sm" variant="outline" className="rounded-full border-sky-200">
                Go to meal plan
              </Button>
            </a>
          </div>
        )}

        {plan && (
          <div className="space-y-2" data-testid="adherence-meals">
            {items.map((it) => (
              <label key={it.key} className="flex items-center gap-3 p-3 rounded-xl bg-emerald-50/40 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isEaten(it.key)}
                  onChange={(e) => toggleMeal(it.key, e.target.checked)}
                  className="w-5 h-5 rounded accent-emerald-600"
                  data-testid={`adherence-check-${it.key}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-emerald-700 font-semibold uppercase tracking-wider">{it.label}</div>
                  <div className="text-sm text-slate-900 truncate">{it.text || "—"}</div>
                </div>
              </label>
            ))}
          </div>
        )}
      </Card>

      <Card className="rounded-3xl border-emerald-100 p-6">
        <h3 className="font-display font-semibold text-slate-900 mb-4">How are you feeling?</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Mood</Label>
            <Select value={note.mood} onValueChange={(v) => setNote({ ...note, mood: v })}>
              <SelectTrigger className="rounded-xl mt-1.5" data-testid="adherence-mood"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="great">Great</SelectItem>
                <SelectItem value="good">Good</SelectItem>
                <SelectItem value="ok">Okay</SelectItem>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="bad">Bad</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Energy</Label>
            <Select value={note.energy} onValueChange={(v) => setNote({ ...note, energy: v })}>
              <SelectTrigger className="rounded-xl mt-1.5" data-testid="adherence-energy"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4">
          <Label>Note</Label>
          <Textarea rows={3} className="rounded-xl mt-1.5" value={note.note}
                    onChange={(e) => setNote({ ...note, note: e.target.value })}
                    placeholder="Anything to remember about today…"
                    data-testid="adherence-note-input" />
        </div>
        <div className="text-right mt-4">
          <Button onClick={saveNote} disabled={savingNote} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="adherence-save-note">
            {savingNote ? "Saving…" : "Save daily note"}
          </Button>
        </div>
      </Card>
    </div>
  );
}

// ============ Helpers ============
function today() {
  return new Date().toISOString().slice(0, 10);
}
function nowHM() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function LineChartCard({ title, data, color = "#10b981", unit = "", testid, bands = [] }) {
  if (!data || data.length === 0) {
    return (
      <Card className="rounded-3xl border-emerald-100 p-6">
        <h3 className="font-display font-semibold text-slate-900 mb-2">{title}</h3>
        <div className="h-32 flex items-center justify-center text-sm text-slate-400">No data yet</div>
      </Card>
    );
  }
  const W = 760, H = 200, P = 28;
  const xs = data.map((_, i) => i);
  const ys = data.map((d) => d.v);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const yRange = Math.max(maxY - minY, 1);
  const xScale = (i) => P + (i / Math.max(xs.length - 1, 1)) * (W - 2 * P);
  const yScale = (v) => H - P - ((v - minY) / yRange) * (H - 2 * P);
  const path = data.map((d, i) => `${i === 0 ? "M" : "L"}${xScale(i)},${yScale(d.v)}`).join(" ");
  return (
    <Card className="rounded-3xl border-emerald-100 p-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-semibold text-slate-900">{title}</h3>
        <div className="text-xs text-slate-500">
          min {minY.toFixed(0)}{unit} • max {maxY.toFixed(0)}{unit} • {data.length} points
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-44">
        {bands.map((b, i) => {
          const y1 = yScale(b.to); const y2 = yScale(b.from);
          return <rect key={i} x={P} y={Math.min(y1, y2)} width={W - 2 * P} height={Math.abs(y2 - y1)} fill={b.color} />;
        })}
        <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {data.map((d, i) => (
          <circle key={i} cx={xScale(i)} cy={yScale(d.v)} r={2.5} fill={color}>
            <title>{`${d.date}: ${d.v}${unit}`}</title>
          </circle>
        ))}
      </svg>
    </Card>
  );
}
